import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import {
  Loader2, CreditCard, Search, Edit2, XCircle, CheckCircle2, Clock, AlertTriangle,
  User, Globe, Calendar, Euro, RefreshCw, Trash2, ExternalLink
} from 'lucide-react';

export const AdminSubscriptions = () => {
  const { token, hasPermission } = useAdminAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [editForm, setEditForm] = useState({
    monthly_price_brutto: '',
    payment_method: '',
    status: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingSub, setCancelingSub] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const canEdit = hasPermission('orders:edit');

  const fetchSubscriptions = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/subscriptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data.subscriptions || []);
      }
    } catch (error) {
      console.error('Subscriptions error:', error);
      toast.error('Fehler beim Laden der Abonnements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, [token]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle2 className="h-3 w-3 mr-1" />Aktiv</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="h-3 w-3 mr-1" />Gekündigt</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500/20 text-yellow-400"><Clock className="h-3 w-3 mr-1" />Pausiert</Badge>;
      case 'past_due':
        return <Badge className="bg-orange-500/20 text-orange-400"><AlertTriangle className="h-3 w-3 mr-1" />Überfällig</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400">{status}</Badge>;
    }
  };

  const getPaymentMethodBadge = (method) => {
    if (method === 'stripe') {
      return <Badge variant="outline" className="border-blue-500/50 text-blue-400">Stripe</Badge>;
    } else if (method === 'paypal') {
      return <Badge variant="outline" className="border-yellow-500/50 text-yellow-400">PayPal</Badge>;
    }
    return <Badge variant="outline" className="border-slate-500/50 text-slate-400">{method || 'Unbekannt'}</Badge>;
  };

  const openEditDialog = (sub) => {
    setEditingSub(sub);
    setEditForm({
      monthly_price_brutto: sub.monthly_price_brutto?.toString() || '',
      payment_method: sub.payment_method || 'stripe',
      status: sub.status || 'active'
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingSub) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/subscriptions/${editingSub.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          monthly_price_brutto: parseFloat(editForm.monthly_price_brutto) || editingSub.monthly_price_brutto,
          payment_method: editForm.payment_method,
          status: editForm.status
        })
      });
      
      if (response.ok) {
        toast.success('Abonnement aktualisiert');
        setEditDialogOpen(false);
        fetchSubscriptions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const openCancelDialog = (sub) => {
    setCancelingSub(sub);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancel = async () => {
    if (!cancelingSub) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/subscriptions/${cancelingSub.id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: cancelReason || 'Admin-Stornierung'
        })
      });
      
      if (response.ok) {
        toast.success('Abonnement gekündigt');
        setCancelDialogOpen(false);
        fetchSubscriptions();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Kündigen');
      }
    } catch (error) {
      toast.error('Fehler beim Kündigen');
    } finally {
      setSaving(false);
    }
  };

  // Filter subscriptions
  const filteredSubs = subscriptions.filter(sub => {
    const matchesSearch = !searchTerm || 
      sub.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.domain?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Stats
  const activeCount = subscriptions.filter(s => s.status === 'active').length;
  const totalMRR = subscriptions
    .filter(s => s.status === 'active')
    .reduce((sum, s) => sum + (s.monthly_price_brutto || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-subscriptions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Abonnements</h1>
          <p className="text-slate-400 mt-1">
            {activeCount} aktive Abonnements · MRR: {formatPrice(totalMRR)}
          </p>
        </div>
        <Button onClick={fetchSubscriptions} variant="outline" className="border-slate-600">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Suche nach Kunde, E-Mail oder Domain..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="cancelled">Gekündigt</SelectItem>
                <SelectItem value="paused">Pausiert</SelectItem>
                <SelectItem value="past_due">Überfällig</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : filteredSubs.length === 0 ? (
            <p className="text-slate-500 text-center py-20">Keine Abonnements gefunden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Kunde</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Domain</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Preis/Monat</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Zahlungsart</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Erstellt</th>
                    {canEdit && (
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktionen</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.map((sub) => (
                    <tr key={sub.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <div>
                            <p className="text-white font-medium">{sub.customer_name || 'Unbekannt'}</p>
                            <p className="text-slate-400 text-sm">{sub.customer_email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-blue-400" />
                          <span className="text-white">{sub.domain || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-green-400 font-semibold">
                          {formatPrice(sub.monthly_price_brutto)}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {getPaymentMethodBadge(sub.payment_method)}
                      </td>
                      <td className="py-4 px-4">
                        {getStatusBadge(sub.status)}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-slate-400">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(sub.created_at)}</span>
                        </div>
                      </td>
                      {canEdit && (
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(sub)}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {sub.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openCancelDialog(sub)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Abonnement bearbeiten</DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingSub?.domain} - {editingSub?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Monatlicher Preis (Brutto)</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.monthly_price_brutto}
                  onChange={(e) => setEditForm({...editForm, monthly_price_brutto: e.target.value})}
                  className="pl-10 bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Zahlungsmethode</Label>
              <Select value={editForm.payment_method} onValueChange={(v) => setEditForm({...editForm, payment_method: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="paused">Pausiert</SelectItem>
                  <SelectItem value="cancelled">Gekündigt</SelectItem>
                  <SelectItem value="past_due">Überfällig</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-600 text-slate-300">
              Abbrechen
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              Abonnement kündigen
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {cancelingSub?.domain} - {cancelingSub?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <p className="text-red-400 text-sm">
                <strong>Achtung:</strong> Diese Aktion kündigt das Abonnement. Der Kunde wird keine weiteren Zahlungen leisten.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Kündigungsgrund (optional)</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="z.B. Kundenwunsch, Zahlungsausfall..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="border-slate-600 text-slate-300">
              Abbrechen
            </Button>
            <Button onClick={handleCancel} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Kündigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
